import {
  workAsyncStorage,
  type WorkStore,
} from '../app-render/work-async-storage.external'
import type { VaryParamsAccumulator } from '../app-render/vary-params'
import {
  createVaryingParams,
  getMetadataVaryParamsAccumulator,
} from '../app-render/vary-params'

import { ReflectAdapter } from '../web/spec-extension/adapters/reflect'
import {
  workUnitAsyncStorage,
  type StaticPrerenderStoreModern,
  type StaticPrerenderStore,
  throwInvariantForMissingStore,
  type PrerenderStoreModernRuntime,
  type RequestStore,
  type ValidationStoreClient,
} from '../app-render/work-unit-async-storage.external'
import { InvariantError } from '../../shared/lib/invariant-error'
import {
  describeStringPropertyAccess,
  wellKnownProperties,
} from '../../shared/lib/utils/reflect-utils'
import {
  makeDevtoolsIOAwarePromise,
  makeFallbackParamsHangingPromise,
  makePromiseFromTrigger,
  trackFallbackParamsAccessed,
  RENDER_STAGES_BY_DATA_KIND,
  trackPromiseUsed,
  trackIncompatibleShellContent,
} from '../dynamic-rendering-utils'
import { createDedupedByCallsiteServerErrorLoggerDev } from '../create-deduped-by-callsite-server-error-logger'
import { abortOnDynamicAccess } from '../app-render/dynamic-access-async-storage.external'
import {
  isEmptyParams,
  hasFallbackRouteParams,
  allParamsAreRootParams,
} from '../lib/params-utils'

export type ParamValue = string | Array<string> | undefined
export type Params = Record<string, ParamValue>

export function createParamsFromClient(
  underlyingParams: Params
): Promise<Params> {
  const workStore = workAsyncStorage.getStore()
  if (!workStore) {
    throw new InvariantError('Expected workStore to be initialized')
  }
  const workUnitStore = workUnitAsyncStorage.getStore()
  if (workUnitStore) {
    switch (workUnitStore.type) {
      case 'prerender':
      case 'prerender-client':
      case 'prerender-legacy':
        // Client params don't need additional vary tracking because by the
        // time they reach the client, the access would have already been
        // tracked by the server.
        const varyParamsAccumulator = null
        return createStaticPrerenderParams(
          underlyingParams,
          null,
          workStore,
          workUnitStore,
          varyParamsAccumulator
        )
      case 'cache':
      case 'private-cache':
      case 'unstable-cache':
        throw new InvariantError(
          'createParamsFromClient should not be called in cache contexts.'
        )
      case 'prerender-runtime':
        throw new InvariantError(
          'createParamsFromClient should not be called in a runtime prerender.'
        )
      case 'build-time-generator':
        throw new InvariantError(
          `createParamsFromClient should not be called inside ${workUnitStore.functionName}.`
        )
      case 'validation-client': {
        if (workUnitStore.validationSamples) {
          return createClientParamsInInstantValidation(
            underlyingParams,
            workStore,
            workUnitStore.validationSamples
          )
        }
        return makeUntrackedParams(underlyingParams)
      }
      case 'request': {
        if (workUnitStore.validationSamples) {
          return createClientParamsInInstantValidation(
            underlyingParams,
            workStore,
            workUnitStore.validationSamples
          )
        }
        if (process.env.NODE_ENV === 'development') {
          const stagedFallbackParams = workUnitStore.stagedFallbackParams
          const userspaceParams = underlyingParams
          return createRenderParamsInDev(
            underlyingParams,
            userspaceParams,
            stagedFallbackParams,
            workStore,
            workUnitStore
          )
        } else {
          return createRenderParamsInProd(underlyingParams)
        }
      }
      default:
        workUnitStore satisfies never
    }
  }
  throwInvariantForMissingStore()
}

// generateMetadata always runs in RSC context so it is equivalent to a Server Page Component
export type CreateServerParamsForMetadata = typeof createServerParamsForMetadata
export function createServerParamsForMetadata(
  underlyingParams: Params,
  optionalCatchAllParamName: string | null
): Promise<Params> {
  const metadataVaryParamsAccumulator = getMetadataVaryParamsAccumulator()
  return createServerParamsForServerSegment(
    underlyingParams,
    optionalCatchAllParamName,
    metadataVaryParamsAccumulator
  )
}

// routes always runs in RSC context so it is equivalent to a Server Page Component
export function createServerParamsForRoute(
  underlyingParams: Params,
  varyParamsAccumulator: VaryParamsAccumulator | null = null
): Promise<Params> {
  const workStore = workAsyncStorage.getStore()
  if (!workStore) {
    throw new InvariantError('Expected workStore to be initialized')
  }
  const workUnitStore = workUnitAsyncStorage.getStore()
  if (workUnitStore) {
    switch (workUnitStore.type) {
      case 'prerender':
      case 'prerender-legacy':
        return createStaticPrerenderParams(
          underlyingParams,
          null,
          workStore,
          workUnitStore,
          varyParamsAccumulator
        )
      case 'prerender-client':
      case 'validation-client':
        throw new InvariantError(
          'createServerParamsForRoute should not be called in client contexts.'
        )
      case 'cache':
      case 'private-cache':
      case 'unstable-cache':
        throw new InvariantError(
          'createServerParamsForRoute should not be called in cache contexts.'
        )
      case 'build-time-generator':
        throw new InvariantError(
          `createServerParamsForRoute should not be called inside ${workUnitStore.functionName}.`
        )
      case 'prerender-runtime': {
        throw new InvariantError(
          'createServerParamsForRoute should not be called in runtime prerenders.'
        )
      }
      case 'request':
        if (process.env.NODE_ENV === 'development') {
          const stagedFallbackParams = workUnitStore.stagedFallbackParams
          const userspaceParams = underlyingParams
          return createRenderParamsInDev(
            underlyingParams,
            userspaceParams,
            stagedFallbackParams,
            workStore,
            workUnitStore
          )
        } else {
          return createRenderParamsInProd(underlyingParams)
        }
      default:
        workUnitStore satisfies never
    }
  }
  throwInvariantForMissingStore()
}

export function createServerParamsForServerSegment(
  underlyingParams: Params,
  optionalCatchAllParamName: string | null,
  varyParamsAccumulator: VaryParamsAccumulator | null
): Promise<Params> {
  const workStore = workAsyncStorage.getStore()
  if (!workStore) {
    throw new InvariantError('Expected workStore to be initialized')
  }
  const workUnitStore = workUnitAsyncStorage.getStore()
  if (workUnitStore) {
    switch (workUnitStore.type) {
      case 'prerender':
      case 'prerender-client':
      case 'prerender-legacy':
        return createStaticPrerenderParams(
          underlyingParams,
          optionalCatchAllParamName,
          workStore,
          workUnitStore,
          varyParamsAccumulator
        )
      case 'validation-client':
        throw new InvariantError(
          'createServerParamsForServerSegment should not be called in client contexts.'
        )
      case 'cache':
      case 'private-cache':
      case 'unstable-cache':
        throw new InvariantError(
          'createServerParamsForServerSegment should not be called in cache contexts.'
        )
      case 'build-time-generator':
        throw new InvariantError(
          `createServerParamsForServerSegment should not be called inside ${workUnitStore.functionName}.`
        )
      case 'prerender-runtime':
        return createRuntimePrerenderParams(
          underlyingParams,
          optionalCatchAllParamName,
          workStore,
          workUnitStore,
          varyParamsAccumulator
        )
      case 'request': {
        return createRenderParamsForPage(
          workStore,
          workUnitStore,
          underlyingParams,
          optionalCatchAllParamName,
          varyParamsAccumulator
        )
      }
      default:
        workUnitStore satisfies never
    }
  }
  throwInvariantForMissingStore()
}

export function createPrerenderParamsForClientSegment(
  underlyingParams: Params
): Promise<Params> {
  const workStore = workAsyncStorage.getStore()
  if (!workStore) {
    throw new InvariantError(
      'Missing workStore in createPrerenderParamsForClientSegment'
    )
  }

  const workUnitStore = workUnitAsyncStorage.getStore()
  if (workUnitStore) {
    switch (workUnitStore.type) {
      case 'prerender':
      case 'prerender-client':
        const fallbackParams = workUnitStore.fallbackRouteParams
        if (fallbackParams) {
          for (let key in underlyingParams) {
            if (fallbackParams.has(key)) {
              // This params object has one or more fallback params, so we need
              // to consider the awaiting of this params object "dynamic". Since
              // we are in cacheComponents mode we encode this as a promise that never
              // resolves.
              return makeFallbackParamsHangingPromise(
                workUnitStore.renderSignal,
                workStore.route,
                '`params`',
                workUnitStore
              )
            }
          }
        }
        break
      case 'validation-client':
        throw new InvariantError(
          'createPrerenderParamsForClientSegment should not be called in validation contexts.'
        )
        break
      case 'cache':
      case 'private-cache':
      case 'unstable-cache':
        throw new InvariantError(
          'createPrerenderParamsForClientSegment should not be called in cache contexts.'
        )
      case 'build-time-generator':
        throw new InvariantError(
          `createPrerenderParamsForClientSegment should not be called inside ${workUnitStore.functionName}.`
        )
      case 'prerender-runtime':
      case 'prerender-legacy':
      case 'request':
        break
      default:
        workUnitStore satisfies never
    }
  }
  // We're prerendering in a mode that does not abort. We resolve the promise without
  // any tracking because we're just transporting a value from server to client where the tracking
  // will be applied.
  return Promise.resolve(underlyingParams)
}

function createStaticPrerenderParams(
  underlyingParams: Params,
  optionalCatchAllParamName: string | null,
  workStore: WorkStore,
  prerenderStore: StaticPrerenderStore,
  varyParamsAccumulator: VaryParamsAccumulator | null
): Promise<Params> {
  switch (prerenderStore.type) {
    case 'prerender': {
      let userspaceParams = underlyingParams
      if (varyParamsAccumulator !== null) {
        userspaceParams = createVaryingParams(
          varyParamsAccumulator,
          underlyingParams,
          optionalCatchAllParamName
        )
      }

      if (isEmptyParams(underlyingParams)) {
        // This route has no params.
        return makeUntrackedParams(userspaceParams)
      }

      const fallbackParams = prerenderStore.fallbackRouteParams
      if (hasFallbackRouteParams(underlyingParams, fallbackParams)) {
        // This params object has one or more fallback params, so we need
        // to consider the awaiting of this params object dynamic.
        return makeHangingParams(underlyingParams, workStore, prerenderStore)
      }

      // All params are static.

      const { stagedRendering } = prerenderStore
      if (stagedRendering) {
        // Even if all params are static, we need to exclude them from the app shell
        // by delaying them to the static stage. However, root params are allowed in shells,
        // so if all the params are root params, they can be included as well.
        if (
          !allParamsAreRootParams(underlyingParams, prerenderStore.rootParams)
        ) {
          const staticParamsStage =
            RENDER_STAGES_BY_DATA_KIND.staticUrlData.static
          return stagedRendering.delayUntilStage(
            staticParamsStage,
            'params',
            userspaceParams
          )
        }
      }

      return makeUntrackedParams(userspaceParams)
    }
    case 'prerender-client': {
      const fallbackParams = prerenderStore.fallbackRouteParams
      if (fallbackParams) {
        for (const key in underlyingParams) {
          if (fallbackParams.has(key)) {
            // This params object has one or more fallback params, so we need
            // to consider the awaiting of this params object "dynamic". Since
            // we are in cacheComponents mode we encode this as a promise that never
            // resolves.

            return makeHangingParams(
              underlyingParams,
              workStore,
              prerenderStore
            )
          }
        }
      }
      break
    }
    case 'prerender-legacy':
      break
    default:
      prerenderStore satisfies never
  }

  let userspaceParams = underlyingParams
  if (varyParamsAccumulator !== null) {
    userspaceParams = createVaryingParams(
      varyParamsAccumulator,
      underlyingParams,
      optionalCatchAllParamName
    )
  }
  return makeUntrackedParams(userspaceParams)
}

function createRuntimePrerenderParams(
  underlyingParams: Params,
  optionalCatchAllParamName: string | null,
  workStore: WorkStore,
  workUnitStore: PrerenderStoreModernRuntime,
  varyParamsAccumulator: VaryParamsAccumulator | null
): Promise<Params> {
  let userspaceParams = underlyingParams
  if (varyParamsAccumulator !== null) {
    userspaceParams = createVaryingParams(
      varyParamsAccumulator,
      underlyingParams,
      optionalCatchAllParamName
    )
  }

  if (isEmptyParams(underlyingParams)) {
    // This route has no params.
    return makeUntrackedParams(userspaceParams)
  }

  // Root params are allowed in shells, so we allow them to resolve without a delay.
  if (allParamsAreRootParams(underlyingParams, workUnitStore.rootParams)) {
    return makeUntrackedParams(userspaceParams)
  }

  // Non-root params are URL data, and we need to recover a param-less session shell,
  // so we delay all params until the PrefetchRuntime stage (even if they're static)
  const paramsStage = RENDER_STAGES_BY_DATA_KIND.runtimeUrlData

  const { stagedRendering } = workUnitStore
  if (!stagedRendering) {
    // If there's no stage controller, we're in a prospective runtime prerender.
    // Make sure we don't unblock content that won't be reached in the final prerender.
    if (workUnitStore.finalStage < paramsStage) {
      return makeHangingParams(underlyingParams, workStore, workUnitStore)
    } else {
      return makeUntrackedParams(userspaceParams)
    }
  }

  // If params don't resolve in this prerender, caches need to treat them as a hanging input.
  if (stagedRendering.finalStage && stagedRendering.finalStage < paramsStage) {
    return makeHangingParams(underlyingParams, workStore, workUnitStore)
  } else {
    return stagedRendering.delayUntilStage(
      paramsStage,
      'params',
      userspaceParams
    )
  }
}

function createRenderParamsForPage(
  workStore: WorkStore,
  workUnitStore: RequestStore,
  underlyingParams: Params,
  optionalCatchAllParamName: string | null,
  varyParamsAccumulator: VaryParamsAccumulator | null
) {
  const { stagedRendering, asyncApiPromises, validationSamples } = workUnitStore

  // Distinguish the params that we expose to userspace (potentially wrapped in proxies)
  // and the underlying object containing params values. We do this because wrappers
  // like `instrumentParamsPromiseWithDevWarnings` need to be able to get the known param names
  // without triggering other wrapper proxies.
  let userspaceParams = underlyingParams
  if (validationSamples) {
    userspaceParams = createServerParamsProxyForInstantValidation(
      underlyingParams,
      workStore,
      validationSamples
    )
  }
  if (varyParamsAccumulator) {
    userspaceParams = createVaryingParams(
      varyParamsAccumulator,
      userspaceParams,
      optionalCatchAllParamName
    )
  }

  if (stagedRendering && asyncApiPromises) {
    return createStagedRenderParams(
      workStore,
      workUnitStore,
      stagedRendering,
      asyncApiPromises,
      underlyingParams,
      userspaceParams
    )
  }

  // No staged rendering = no cacheComponents, or cacheComponents prod without cachedNavigations
  if (process.env.NODE_ENV === 'development') {
    const stagedFallbackParams = workUnitStore.stagedFallbackParams
    return createRenderParamsInDev(
      underlyingParams,
      userspaceParams,
      stagedFallbackParams,
      workStore,
      workUnitStore
    )
  } else {
    return createRenderParamsInProd(userspaceParams)
  }
}

function createStagedRenderParams(
  workStore: WorkStore,
  workUnitStore: RequestStore,
  stagedRendering: NonNullable<RequestStore['stagedRendering']>,
  asyncApiPromises: NonNullable<RequestStore['asyncApiPromises']>,
  underlyingParams: Params,
  userspaceParams: Params
) {
  const promise = createStagedRenderParamsImpl(
    workUnitStore,
    stagedRendering,
    asyncApiPromises,
    underlyingParams,
    userspaceParams
  )
  if (process.env.NODE_ENV === 'development') {
    return instrumentParamsPromiseWithDevWarnings(
      underlyingParams,
      promise,
      workStore
    )
  } else {
    return promise
  }
}

function createStagedRenderParamsImpl(
  workUnitStore: RequestStore,
  stagedRendering: NonNullable<RequestStore['stagedRendering']>,
  asyncApiPromises: NonNullable<RequestStore['asyncApiPromises']>,
  /** The actual param values, without any instrumentation */
  underlyingParams: Params,
  /** The params object to return to userspace, possibly wrapped in a proxy */
  userspaceParams: Params
) {
  // If the route has no params, they should resolve immediately.
  if (isEmptyParams(underlyingParams)) {
    return makeUntrackedParams(userspaceParams)
  }

  // If we have fallback params, then they should always resolve in the runtime link data stage.
  // We do this indirectly via the shared params parent for better debug info.
  if (
    hasFallbackRouteParams(underlyingParams, workUnitStore.stagedFallbackParams)
  ) {
    return createParamsPromiseFromTrigger(
      asyncApiPromises.sharedParamsParent,
      userspaceParams
    )
  }

  // All params are static.

  // If we're rendering with shells, even static params must be delayed to exclude them from the shell.
  // However, root params are allowed in shells, so if all the params are root params, they can be included as well.
  if (!allParamsAreRootParams(underlyingParams, workUnitStore.rootParams)) {
    // For a dynamic request we generally want to recover a static shell,
    // so static params can resolve in the static stage, because session
    // shells are handled with a separate render.
    // However, in dev we might need to recover a session shell for instant validation.
    // This is indicated by `needsRuntimeShell`.
    const staticParamsStage =
      RENDER_STAGES_BY_DATA_KIND.staticUrlData[
        workUnitStore.needsRuntimeShell ? 'runtime' : 'static'
      ]

    const promise = stagedRendering.delayUntilStage(
      staticParamsStage,
      'params',
      userspaceParams
    )
    if (process.env.__NEXT_DEV_SERVER) {
      // If static params are accessed, we can recover a static shell or a session shell, but not both.
      return trackPromiseUsed(
        promise,
        trackIncompatibleShellContent.bind(
          null,
          workUnitStore,
          'static `params`'
        )
      )
    } else {
      return promise
    }
  }

  return makeUntrackedParams(userspaceParams)
}

function createParamsPromiseFromTrigger(
  trigger: Promise<any>,
  userspaceParams: Params
) {
  if (process.env.NODE_ENV === 'development') {
    // We wrap each instance of params in a `new Promise()`, which lets us show each
    // await a different set of values. This is important when all awaits
    // are in third party which would otherwise track all the way to the
    // internal params.
    const promise: Promise<Params> = new Promise((resolve, reject) => {
      trigger.then(() => resolve(userspaceParams), reject)
    })
    promise.catch(noop)
    // @ts-expect-error
    promise.displayName = 'params'
    return promise
  } else {
    return makePromiseFromTrigger(trigger, userspaceParams)
  }
}

function noop() {}

function createServerParamsProxyForInstantValidation(
  underlyingParams: Params,
  workStore: WorkStore,
  validationSamples: NonNullable<RequestStore['validationSamples']>
): Params {
  const { createExhaustiveParamsProxy } =
    require('../app-render/instant-validation/instant-samples') as typeof import('../app-render/instant-validation/instant-samples')
  const declaredParams = new Set(Object.keys(validationSamples.params ?? {}))
  return createExhaustiveParamsProxy(
    underlyingParams,
    declaredParams,
    workStore.route
  )
}

function createClientParamsInInstantValidation(
  underlyingParams: Params,
  workStore: WorkStore,
  validationSamples: ValidationStoreClient['validationSamples']
): Promise<Params> {
  const { createExhaustiveParamsProxy } =
    require('../app-render/instant-validation/instant-samples') as typeof import('../app-render/instant-validation/instant-samples')
  const declaredParams = new Set(Object.keys(validationSamples?.params ?? {}))
  const proxiedUnderlying = createExhaustiveParamsProxy(
    underlyingParams,
    declaredParams,
    workStore.route
  )
  return Promise.resolve(proxiedUnderlying)
}

function createRenderParamsInProd(userspaceParams: Params): Promise<Params> {
  return makeUntrackedParams(userspaceParams)
}

function createRenderParamsInDev(
  underlyingParams: Params,
  userpaceParams: Params,
  stagedFallbackParams: ReadonlySet<string> | null | undefined,
  workStore: WorkStore,
  requestStore: RequestStore
): Promise<Params> {
  return makeDynamicallyTrackedParamsWithDevWarnings(
    underlyingParams,
    userpaceParams,
    hasFallbackRouteParams(underlyingParams, stagedFallbackParams),
    workStore,
    requestStore
  )
}

interface CacheLifetime {}
const CachedParams = new WeakMap<CacheLifetime, Promise<Params>>()

const fallbackParamsProxyHandler: ProxyHandler<Promise<Params>> = {
  get: function get(target, prop, receiver) {
    if (prop === 'then' || prop === 'catch' || prop === 'finally') {
      const originalMethod = ReflectAdapter.get(target, prop, receiver)

      return {
        [prop]: (...args: unknown[]) => {
          // Record against the store that's active at access time: the
          // hanging promise is cached by params object across prerender
          // stores, so the store that created it may not be the one that's
          // rendering when it's finally awaited.
          const workUnitStore = workUnitAsyncStorage.getStore()
          if (workUnitStore !== undefined) {
            trackFallbackParamsAccessed(workUnitStore, '`params`')
          }

          abortOnDynamicAccess(
            'fallback-params',
            new Error(`Accessed fallback \`params\` during prerendering.`)
          )

          return new Proxy(
            originalMethod.apply(target, args),
            fallbackParamsProxyHandler
          )
        },
      }[prop]
    }

    return ReflectAdapter.get(target, prop, receiver)
  },
}

function makeHangingParams(
  underlyingParams: Params,
  workStore: WorkStore,
  prerenderStore: StaticPrerenderStoreModern | PrerenderStoreModernRuntime
): Promise<Params> {
  const cachedParams = CachedParams.get(underlyingParams)
  if (cachedParams) {
    return cachedParams
  }

  const promise = new Proxy(
    makeFallbackParamsHangingPromise<Params>(
      prerenderStore.renderSignal,
      workStore.route,
      '`params`',
      // Passing `null` for the store disables tracking of params usage.
      // Caches need the additional logic from `fallbackParamsProxyHandler`,
      // so we track params usage there instead.
      null
    ),
    fallbackParamsProxyHandler
  )

  CachedParams.set(underlyingParams, promise)

  return promise
}

function makeUntrackedParams(underlyingParams: Params): Promise<Params> {
  const cachedParams = CachedParams.get(underlyingParams)
  if (cachedParams) {
    return cachedParams
  }

  const promise = Promise.resolve(underlyingParams)
  CachedParams.set(underlyingParams, promise)

  return promise
}

function makeDynamicallyTrackedParamsWithDevWarnings(
  underlyingParams: Params,
  userspaceParams: Params,
  hasFallbackParams: boolean,
  workStore: WorkStore,
  requestStore: RequestStore
): Promise<Params> {
  const cachedParams = CachedParams.get(underlyingParams)
  if (cachedParams) {
    return cachedParams
  }

  // We don't use makeResolvedReactPromise here because params
  // supports copying with spread and we don't want to unnecessarily
  // instrument the promise with spreadable properties of ReactPromise.
  const promise = hasFallbackParams
    ? makeDevtoolsIOAwarePromise(
        userspaceParams,
        requestStore,
        RENDER_STAGES_BY_DATA_KIND.runtimeUrlData
      )
    : // We don't want to force an environment transition when this params is not part of the fallback params set
      Promise.resolve(userspaceParams)

  const proxiedPromise = instrumentParamsPromiseWithDevWarnings(
    underlyingParams,
    promise,
    workStore
  )
  CachedParams.set(underlyingParams, proxiedPromise)
  return proxiedPromise
}

function instrumentParamsPromiseWithDevWarnings(
  underlyingParams: Params,
  promise: Promise<Params>,
  workStore: WorkStore
): Promise<Params> {
  // Track which properties we should warn for.
  const proxiedProperties = new Set<string>()

  Object.keys(underlyingParams).forEach((prop) => {
    if (wellKnownProperties.has(prop)) {
      // These properties cannot be shadowed because they need to be the
      // true underlying value for Promises to work correctly at runtime
    } else {
      proxiedProperties.add(prop)
    }
  })

  return new Proxy(promise, {
    get(target, prop, receiver) {
      if (typeof prop === 'string') {
        if (
          // We are accessing a property that was proxied to the promise instance
          proxiedProperties.has(prop)
        ) {
          const expression = describeStringPropertyAccess('params', prop)
          warnForSyncAccess(workStore.route, expression)
        }
      }
      return ReflectAdapter.get(target, prop, receiver)
    },
    set(target, prop, value, receiver) {
      if (typeof prop === 'string') {
        proxiedProperties.delete(prop)
      }
      return ReflectAdapter.set(target, prop, value, receiver)
    },
    ownKeys(target) {
      const expression = '`...params` or similar expression'
      warnForSyncAccess(workStore.route, expression)
      return Reflect.ownKeys(target)
    },
  })
}

const warnForSyncAccess = createDedupedByCallsiteServerErrorLoggerDev(
  createParamsAccessError
)

function createParamsAccessError(
  route: string | undefined,
  expression: string
) {
  const prefix = route ? `Route "${route}" ` : 'This route '
  return new Error(
    `${prefix}used ${expression}. ` +
      `\`params\` is a Promise and must be unwrapped with \`await\` or \`React.use()\` before accessing its properties. ` +
      `Learn more: https://nextjs.org/docs/messages/sync-dynamic-apis`
  )
}
